import LiquidationKeyFilter from "./LiquidationKeyFilter";
import ThemedElement from "shared/components/ThemedElement";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  CircularProgress,
  Alert,
  Card,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Snackbar,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Search,
  ContentCopy,
  Check,
  PictureAsPdf,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Toll as TollIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import DataTable from "shared/components/Tables/DataTable";
import {
  getConciliationDetailByIdAndConcession,
  getConciliationFilesById,
  getNovelties,
} from "modules/finops/conciliaciones/services/financieraService";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  white: "#ffffff",
  success: "#27ae60",
  successLight: "#e8f5e9",
  successDark: "#1e7e34",
  warning: "#ed6c02",
  info: "#2980b9",
  error: "#d32f2f",
  errorLight: "#fdecea",
  errorDark: "#a93226",
  gray: "#f8fafc",
  border: "#e2e8f0",
  text: "#1e293b",
  textSecondary: "#64748b",
  purple: "#8e44ad",
  blue: "#2980b9",
  blueDark: "#1e5f8a",
  blueLight: "#eaf3fb",
  peaje: "#1a4b8c",
  peajeBg: "#e8f0fe",
  peajeBorder: "#b8cef5",
};

// ================================================
// HELPER TIPO CONCILIACIÓN
// ================================================
const normalizarTipoConcil = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return 1;
  return String(value).trim() === "2" ? 2 : 1;
};

// ================================================
// CELDA CON BOTÓN COPIAR
// ================================================
const CopyableCell = ({ value, maxChars = 15, monoSize = "0.65rem", onCopy = null }) => {
  const [copied, setCopied] = useState(false);

  const isEmpty = !value || value === "-" || value === "" || value === null || value === undefined;

  const handleCopy = () => {
    if (isEmpty) return;

    const doCopy = () => {
      setCopied(true);
      if (onCopy) onCopy(value);
      setTimeout(() => setCopied(false), 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(doCopy)
        .catch(() => {
          const textArea = document.createElement("textarea");
          textArea.value = value;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          doCopy();
        });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      doCopy();
    }
  };

  if (isEmpty) {
    return (
      <Typography
        variant="caption"
        sx={{ fontStyle: "italic", color: colors.textSecondary, fontSize: "0.7rem" }}
      >
        N/A
      </Typography>
    );
  }

  const strValue = String(value);
  const truncated = strValue.length > maxChars ? `${strValue.substring(0, maxChars)}...` : strValue;

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Tooltip title={strValue} placement="top">
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            fontSize: monoSize,
            backgroundColor: "#f8fafc",
            padding: "2px 4px",
            borderRadius: "4px",
            display: "inline-block",
            maxWidth: "140px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer",
            color: "#475569",
          }}
        >
          {truncated}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? "¡Copiado!" : "Copiar"} placement="top">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            padding: "2px",
            color: copied ? colors.success : colors.info,
            "&:hover": {
              backgroundColor: copied ? `${colors.success}15` : `${colors.info}15`,
            },
          }}
        >
          {copied ? <Check sx={{ fontSize: 13 }} /> : <ContentCopy sx={{ fontSize: 13 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

CopyableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxChars: PropTypes.number,
  monoSize: PropTypes.string,
  onCopy: PropTypes.func,
};

CopyableCell.defaultProps = {
  value: "",
  maxChars: 15,
  monoSize: "0.65rem",
  onCopy: null,
};

// ================================================
// CÓDIGO PASO CELL
// ================================================
const CodigoPasoCell = ({ value, onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopied(true);
          if (onCopy) onCopy(value);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          const textArea = document.createElement("textarea");
          textArea.value = value;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          setCopied(true);
          if (onCopy) onCopy(value);
          setTimeout(() => setCopied(false), 2000);
        });
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography
        variant="caption"
        sx={{
          fontFamily: "monospace",
          fontSize: "0.7rem",
          backgroundColor: "#f1f5f9",
          padding: "2px 6px",
          borderRadius: "4px",
          display: "inline-block",
          maxWidth: "180px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "N/A"}
      </Typography>
      {value && (
        <Tooltip title={copied ? "¡Copiado!" : "Copiar código"} placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              padding: "2px",
              color: copied ? colors.success : colors.info,
              "&:hover": {
                backgroundColor: copied ? `${colors.success}15` : `${colors.info}15`,
              },
            }}
          >
            {copied ? <Check sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

CodigoPasoCell.propTypes = {
  value: PropTypes.string,
  onCopy: PropTypes.func,
};
CodigoPasoCell.defaultProps = { value: "", onCopy: null };

// ================================================
// TIPO CONCILIACIÓN CELL
// ================================================
const TipoConcilCell = ({ value }) => {
  const tipo = normalizarTipoConcil(value);
  const color = tipo === 2 ? colors.purple : colors.info;

  return (
    <Chip
      label={`Tipo ${tipo}`}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        backgroundColor: `${color}15`,
        color,
        border: `1px solid ${color}30`,
      }}
    />
  );
};

TipoConcilCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
TipoConcilCell.defaultProps = { value: null };

// ================================================
// PEAJE CELL (vista Información de concesión)
// ================================================
const PeajeCell = ({ value }) => {
  const nombre = value?.name || value?.nombre || value;

  if (!nombre) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
        N/A
      </Typography>
    );
  }

  return (
    <Chip
      label={nombre}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.7rem",
        bgcolor: colors.peajeBg,
        color: colors.peaje,
        border: `1px solid ${colors.peajeBorder}`,
        maxWidth: "100%",
        "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
      }}
    />
  );
};

PeajeCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
PeajeCell.defaultProps = { value: null };

// ================================================
// VALORES MONETARIOS
// ================================================
const ValorMonedaCell = ({ value }) => (
  <Typography
    variant="body2"
    fontWeight="600"
    color="primary.main"
    sx={{ fontSize: "0.85rem", fontFamily: "monospace" }}
  >
    ${parseFloat(value || 0).toLocaleString("es-CO")}
  </Typography>
);

ValorMonedaCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
ValorMonedaCell.defaultProps = { value: 0 };

const ValorPagarCell = ({ value }) => (
  <Typography
    variant="body2"
    fontWeight="bold"
    color="secondary.main"
    sx={{ fontSize: "0.9rem", fontFamily: "monospace" }}
  >
    ${parseFloat(value || 0).toLocaleString("es-CO")}
  </Typography>
);

ValorPagarCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
ValorPagarCell.defaultProps = { value: 0 };

// ================================================
// FECHA FINOPS CELL
// ================================================
const FechaFinopsCell = ({ filesData }) => {
  if (!filesData || filesData.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
        Sin archivos
      </Typography>
    );
  }

  try {
    const firstFile = filesData[0];
    const dateValue = firstFile.createdAt || firstFile.created_at;

    if (!dateValue) {
      return (
        <Typography variant="caption" color="text.secondary">
          Fecha no disponible
        </Typography>
      );
    }

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return (
        <Typography variant="caption" color="text.secondary">
          {dateValue}
        </Typography>
      );
    }

    const fileName =
      firstFile.file?.originalname || firstFile.filename || firstFile.file?.filename || "Documento";

    return (
      <Tooltip
        title={`${fileName} - ${date.toLocaleString("es-CO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}`}
        placement="top"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
            {date.toLocaleDateString("es-CO", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })}
          </Typography>
          <PictureAsPdf sx={{ fontSize: 14, color: colors.error }} />
          <Chip
            label={filesData.length}
            size="small"
            sx={{
              height: 16,
              fontSize: "0.6rem",
              fontWeight: 600,
              backgroundColor: `${colors.info}15`,
              color: colors.info,
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        </Box>
      </Tooltip>
    );
  } catch (error) {
    console.error("Error al formatear fecha FINOPS:", error);
    return (
      <Typography variant="caption" color="text.secondary">
        Error en fecha
      </Typography>
    );
  }
};

FechaFinopsCell.propTypes = { filesData: PropTypes.array };
FechaFinopsCell.defaultProps = { filesData: [] };

// ================================================
// FECHA PASO CELL
// ================================================
const FechaPasoCell = ({ value }) => {
  if (!value) return <Typography variant="caption">N/A</Typography>;

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return <Typography variant="caption">{String(value)}</Typography>;
  }

  return (
    <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
      {date.toLocaleString("es-CO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}
    </Typography>
  );
};

FechaPasoCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};
FechaPasoCell.defaultProps = { value: null };

// ================================================
// LLAVE CONCILIACIÓN CELL
// ================================================
const LlaveConciliacionCell = ({ value }) => (
  <Tooltip title={value || "N/A"} placement="top">
    <Typography
      variant="caption"
      sx={{
        fontSize: "0.7rem",
        color: colors.textSecondary,
        display: "inline-block",
        maxWidth: "150px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: "pointer",
      }}
    >
      {value || "N/A"}
    </Typography>
  </Tooltip>
);

LlaveConciliacionCell.propTypes = { value: PropTypes.string };
LlaveConciliacionCell.defaultProps = { value: "" };

// ================================================
// WRAPPERS PARA DATATABLE
// ================================================
const CodigoPasoWrapper = ({ row }) => (
  <CodigoPasoCell value={row.original.codigopaso} onCopy={row.original.onCopy} />
);
CodigoPasoWrapper.propTypes = { row: PropTypes.object.isRequired };

const IdTransactionWrapper = ({ row }) => (
  <CopyableCell value={row.original.idtransaction} maxChars={12} onCopy={row.original.onCopy} />
);
IdTransactionWrapper.propTypes = { row: PropTypes.object.isRequired };

const TipoConcilWrapper = ({ row }) => <TipoConcilCell value={row.original.tipo_concli} />;
TipoConcilWrapper.propTypes = { row: PropTypes.object.isRequired };

const CufeWrapper = ({ row }) => (
  <CopyableCell value={row.original.cufe} maxChars={15} onCopy={row.original.onCopy} />
);
CufeWrapper.propTypes = { row: PropTypes.object.isRequired };

const FacturaWrapper = ({ row }) => (
  <CopyableCell
    value={row.original.factura}
    maxChars={15}
    monoSize="0.75rem"
    onCopy={row.original.onCopy}
  />
);
FacturaWrapper.propTypes = { row: PropTypes.object.isRequired };

// En Información de concesión el comercio se muestra bajo el nombre «Peaje».
const PeajeWrapper = ({ row }) => <PeajeCell value={row.original.comercio ?? row.original.peaje} />;
PeajeWrapper.propTypes = { row: PropTypes.object.isRequired };

const ValorInicialWrapper = ({ row }) => (
  <ValorMonedaCell value={row.original.valor_inicial || row.original.valorservicio} />
);
ValorInicialWrapper.propTypes = { row: PropTypes.object.isRequired };

const ValorAjusteWrapper = ({ row }) => <ValorMonedaCell value={row.original.valor_ajuste} />;
ValorAjusteWrapper.propTypes = { row: PropTypes.object.isRequired };

const ValorAbonoWrapper = ({ row }) => <ValorMonedaCell value={row.original.valor_abono} />;
ValorAbonoWrapper.propTypes = { row: PropTypes.object.isRequired };

const ValorPagarWrapper = ({ row }) => (
  <ValorPagarCell value={row.original.valor_a_pagar || row.original.valorPagar} />
);
ValorPagarWrapper.propTypes = { row: PropTypes.object.isRequired };

const FechaPasoWrapper = ({ row }) => <FechaPasoCell value={row.original.fecha_paso} />;
FechaPasoWrapper.propTypes = { row: PropTypes.object.isRequired };

const LlaveConciliacionWrapper = ({ row }) => (
  <LlaveConciliacionCell value={row.original.llave_conciliacion} />
);
LlaveConciliacionWrapper.propTypes = { row: PropTypes.object.isRequired };

const FechaFinopsWrapper = ({ row }) => {
  const filesData = row?.original?.files_data || [];
  return <FechaFinopsCell filesData={filesData} />;
};
FechaFinopsWrapper.propTypes = { row: PropTypes.object.isRequired };

// ================================================
// COMPONENTE NOVEDADES
// ================================================
const thStyle = {
  padding: "10px 12px",
  textAlign: "left",
  fontWeight: 700,
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  borderBottom: `2px solid ${colors.blue}`,
  color: colors.blueDark,
};

const tdStyle = {
  padding: "8px 12px",
  fontSize: "0.78rem",
  color: "#334155",
  verticalAlign: "middle",
};

export function NovedadesTable({ keyConcil, concesion }) {
  const [novedades, setNovedades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!keyConcil || !concesion) {
      setNovedades([]);
      return;
    }

    let active = true;
    const controller = new AbortController();
    setNovedades([]);
    setLoading(true);
    setError(null);

    getNovelties(keyConcil, concesion, controller.signal)
      .then((result) => {
        if (!active) return;
        if (result.success) {
          setNovedades(result.data || []);
        } else {
          setError(result.message || "Error al cargar novedades");
          setNovedades([]);
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "Error al cargar novedades");
        setNovedades([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [keyConcil, concesion]);

  const totalValor = useMemo(
    () =>
      novedades.reduce((acc, n) => {
        const v = Number(n.value) || 0;
        const tipo = Number(n.type_nov);
        if (tipo === 1) return acc + v;
        if (tipo === 2) return acc - v;
        return acc + v;
      }, 0),
    [novedades]
  );

  return (
    <Box
      sx={{
        mt: 2,
        mx: 2,
        mb: 2,
        borderRadius: "12px",
        border: `2px solid ${colors.blue}`,
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.blueDark} 100%)`,
          color: "#fff",
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <WarningIcon sx={{ fontSize: 24 }} />
          <MDTypography variant="h6" fontWeight="bold" color="white">
            Novedades
          </MDTypography>
          {!loading && !error && (
            <Chip
              label={`${novedades.length} novedad(es)`}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 600,
              }}
            />
          )}
          {keyConcil && (
            <Chip
              label={`Llave: ${keyConcil}`}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: "monospace",
                fontWeight: 600,
              }}
            />
          )}
          {concesion && (
            <Chip
              label={`Concesión: ${concesion}`}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: "monospace",
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {!loading && !error && novedades.length > 0 && (
            <Chip
              label={`Total: $${totalValor.toLocaleString("es-CO", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}`}
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.25)",
                color: "#fff",
                fontWeight: 700,
              }}
            />
          )}
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ color: "#fff" }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
            sx={{ bgcolor: colors.blueLight }}
          >
            <CircularProgress size={28} sx={{ color: colors.blue }} />
            <Typography variant="caption" sx={{ ml: 1.5, color: colors.blue }}>
              Cargando novedades...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            <MDTypography variant="body2" fontWeight="bold">
              Error al cargar las novedades
            </MDTypography>
            <MDTypography variant="caption">{error}</MDTypography>
          </Alert>
        ) : novedades.length === 0 ? (
          <Box p={3} textAlign="center" sx={{ bgcolor: colors.blueLight }}>
            <Typography variant="body2" sx={{ color: colors.blueDark, fontStyle: "italic" }}>
              No hay novedades registradas para esta conciliación
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <ThemedElement
              component="table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.8rem",
              }}
            >
              <thead>
                <ThemedElement component="tr" style={{ backgroundColor: colors.blueLight }}>
                  <ThemedElement component="th" style={thStyle}>
                    #
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    ID
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    Código
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    Llave
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    Tipo Nov.
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    Valor
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    Peaje
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    Observación
                  </ThemedElement>
                  <ThemedElement component="th" style={thStyle}>
                    Fecha
                  </ThemedElement>
                </ThemedElement>
              </thead>
              <tbody>
                {novedades.map((n, idx) => {
                  const tipo = Number(n.type_nov);
                  const esAbono = tipo === 1;
                  const esAjuste = tipo === 2;

                  const rowBg = esAbono
                    ? colors.successLight
                    : esAjuste
                    ? colors.errorLight
                    : idx % 2 === 0
                    ? "#fff"
                    : "#f8fafc";

                  const rowColor = esAbono
                    ? colors.successDark
                    : esAjuste
                    ? colors.errorDark
                    : "#334155";

                  const chipBg = esAbono ? `${colors.success}25` : `${colors.error}25`;
                  const chipColor = esAbono ? colors.successDark : colors.errorDark;
                  const chipBorder = esAbono ? `${colors.success}60` : `${colors.error}60`;
                  const chipLabel = esAbono ? "Tipo 1 — Abono" : "Tipo 2 — Ajuste";
                  const signo = esAbono ? "+" : esAjuste ? "−" : "";

                  return (
                    <ThemedElement
                      component="tr"
                      key={n.id ?? idx}
                      style={{
                        backgroundColor: rowBg,
                        borderBottom: `1px solid ${colors.border}`,
                        color: rowColor,
                      }}
                    >
                      <ThemedElement
                        component="td"
                        style={{ ...tdStyle, color: rowColor, fontWeight: 600 }}
                      >
                        {idx + 1}
                      </ThemedElement>
                      <ThemedElement component="td" style={{ ...tdStyle, color: rowColor }}>
                        {n.id ?? "N/A"}
                      </ThemedElement>
                      <ThemedElement
                        component="td"
                        style={{
                          ...tdStyle,
                          fontFamily: "monospace",
                          color: rowColor,
                          fontWeight: 600,
                        }}
                      >
                        {n.code ?? "N/A"}
                      </ThemedElement>
                      <ThemedElement
                        component="td"
                        style={{ ...tdStyle, fontFamily: "monospace", color: rowColor }}
                      >
                        {n.key_concil ?? "N/A"}
                      </ThemedElement>
                      <ThemedElement component="td" style={tdStyle}>
                        <Chip
                          label={chipLabel}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            bgcolor: chipBg,
                            color: chipColor,
                            border: `1px solid ${chipBorder}`,
                          }}
                        />
                      </ThemedElement>
                      <ThemedElement
                        component="td"
                        style={{
                          ...tdStyle,
                          fontWeight: 700,
                          color: rowColor,
                          fontFamily: "monospace",
                        }}
                      >
                        {signo}${(Number(n.value) || 0).toLocaleString("es-CO")}
                      </ThemedElement>

                      {/* 🆕 Peaje */}
                      <ThemedElement component="td" style={{ ...tdStyle, color: rowColor }}>
                        {n.peaje ? (
                          <Chip
                            icon={<TollIcon sx={{ fontSize: "14px !important" }} />}
                            label={n.peaje}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              bgcolor: colors.peajeBg,
                              color: colors.peaje,
                              border: `1px solid ${colors.peajeBorder}`,
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8", fontStyle: "italic", fontSize: "0.7rem" }}
                          >
                            Sin peaje
                          </Typography>
                        )}
                      </ThemedElement>

                      <ThemedElement
                        component="td"
                        style={{ ...tdStyle, maxWidth: 260, color: rowColor }}
                      >
                        <Tooltip title={n.observation || ""} placement="top">
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: 240,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {n.observation || "N/A"}
                          </span>
                        </Tooltip>
                      </ThemedElement>
                      <ThemedElement
                        component="td"
                        style={{ ...tdStyle, fontSize: "0.72rem", color: rowColor }}
                      >
                        {n.createdAt
                          ? new Date(n.createdAt).toLocaleString("es-CO", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "N/A"}
                      </ThemedElement>
                    </ThemedElement>
                  );
                })}
              </tbody>
            </ThemedElement>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

NovedadesTable.propTypes = {
  keyConcil: PropTypes.string,
  concesion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

NovedadesTable.defaultProps = {
  keyConcil: null,
  concesion: null,
};

// ================================================
// COMPONENTE PRINCIPAL
// ================================================
function TableDetalleFinanciera1({
  valorFinalFinops,
  conciliationId,
  detailReference,
  concesion,
  refreshFlag,
  llaveConciliacion,
  showNovelties,
  showLiquidationKeys = false,
  vistaConcesion = false,
}) {
  const [allRecords, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [valorTotal, setValorTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [liquidationKey, setLiquidationKey] = useState("");
  useEffect(() => {
    setLiquidationKey("");
  }, [conciliationId, detailReference, concesion, llaveConciliacion]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCopySuccess = useCallback((value) => {
    const strValue = String(value);
    setSnackbar({
      open: true,
      message: `✅ Copiado: ${strValue.length > 40 ? `${strValue.substring(0, 40)}...` : strValue}`,
      severity: "success",
    });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchRecords = useCallback(
    async (signal) => {
      if (!(detailReference || llaveConciliacion || conciliationId) || !concesion) {
        setRecords([]);
        setTotalRecords(0);
        setValorTotal(0);
        return;
      }

      setRecords([]);
      setTotalRecords(0);
      setValorTotal(0);
      setLoading(true);
      setError(null);

      try {
        const result = await getConciliationDetailByIdAndConcession(
          detailReference || llaveConciliacion || conciliationId,
          concesion,
          {},
          signal
        );
        if (signal.aborted) return;

        if (result.success) {
          let data = result.data || [];

          setLoadingFiles(true);
          let archivos = [];
          try {
            const filesResult = await getConciliationFilesById(conciliationId, concesion, signal);
            if (signal.aborted) return;
            if (filesResult.success && filesResult.data) {
              archivos = filesResult.data;
            }
          } catch (error) {
            console.error("Error cargando archivos:", error);
          }
          setLoadingFiles(false);

          data = data.map((item) => ({
            ...item,
            tipo_concli: normalizarTipoConcil(item.tipo_concli),
            files_data: archivos,
            onCopy: handleCopySuccess,
          }));

          setRecords(data);
          setTotalRecords(result.totalRegistros || data.length);
          setValorTotal(result.valorTotal || 0);
        } else {
          throw new Error(result.message || "Error al cargar detalle");
        }
      } catch (err) {
        if (signal.aborted) return;
        const errorMsg = err.message || "Error al cargar detalles";
        console.error("❌ Error en fetchRecords:", err);
        setError(errorMsg);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [conciliationId, detailReference, llaveConciliacion, concesion, handleCopySuccess]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchRecords(controller.signal);
    return () => controller.abort();
  }, [fetchRecords, refreshFlag]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const columns = useMemo(() => {
    const columnasBase = [
      { Header: "Placa", accessor: "placa", width: "6%" },
      { Header: "Tipo", accessor: "tipo_concli", width: "5%", Cell: TipoConcilWrapper },
      { Header: "Código Paso", accessor: "codigopaso", width: "11%", Cell: CodigoPasoWrapper },
      {
        Header: "ID Transacción",
        accessor: "idtransaction",
        width: "10%",
        Cell: IdTransactionWrapper,
      },
      {
        Header: "Valor Inicial",
        accessor: "valor_inicial",
        width: "7%",
        Cell: ValorInicialWrapper,
      },
      { Header: "Valor Ajuste", accessor: "valor_ajuste", width: "6%", Cell: ValorAjusteWrapper },
      { Header: "Valor Abono", accessor: "valor_abono", width: "6%", Cell: ValorAbonoWrapper },
      { Header: "Valor a Pagar", accessor: "valor_a_pagar", width: "7%", Cell: ValorPagarWrapper },
      { Header: "Comercio", accessor: "comercio", width: "9%" },
      { Header: "Concesión", accessor: "concesion", width: "6%" },
      { Header: "Fecha Paso", accessor: "fecha_paso", width: "9%", Cell: FechaPasoWrapper },
      { Header: "Factura", accessor: "factura", width: "8%", Cell: FacturaWrapper },
      { Header: "CUFE", accessor: "cufe", width: "9%", Cell: CufeWrapper },
      {
        Header: "Llave Conciliación",
        accessor: "llave_conciliacion",
        width: "11%",
        Cell: LlaveConciliacionWrapper,
      },
      {
        Header: "Fecha Carga Documento",
        accessor: "files_data",
        width: "10%",
        Cell: FechaFinopsWrapper,
      },
    ];

    if (!vistaConcesion) return columnasBase;

    // Información de concesión: fecha de paso en el lugar de peaje y el
    // comercio (como «Peaje») junto a la concesión; sin factura ni CUFE.
    return [
      { Header: "Placa", accessor: "placa", width: "6%" },
      { Header: "Código Paso", accessor: "codigopaso", width: "11%", Cell: CodigoPasoWrapper },
      { Header: "Fecha Paso", accessor: "fecha_paso", width: "12%", Cell: FechaPasoWrapper },
      {
        Header: "Valor Inicial",
        accessor: "valor_inicial",
        width: "7%",
        Cell: ValorInicialWrapper,
      },
      { Header: "Valor Ajuste", accessor: "valor_ajuste", width: "6%", Cell: ValorAjusteWrapper },
      { Header: "Valor Abono", accessor: "valor_abono", width: "6%", Cell: ValorAbonoWrapper },
      { Header: "Valor a Pagar", accessor: "valor_a_pagar", width: "7%", Cell: ValorPagarWrapper },
      { Header: "Concesión", accessor: "concesion", width: "6%" },
      { Header: "Peaje", accessor: "comercio", width: "11%", Cell: PeajeWrapper },
      {
        Header: "Llave Conciliación",
        accessor: "llave_conciliacion",
        width: "11%",
        Cell: LlaveConciliacionWrapper,
      },
      {
        Header: "Fecha Carga Documento",
        accessor: "files_data",
        width: "10%",
        Cell: FechaFinopsWrapper,
      },
    ];
  }, [vistaConcesion]);

  const records = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    return allRecords
      .filter(
        (item) =>
          !showLiquidationKeys ||
          !liquidationKey ||
          String(item.llave_conciliacion || "").trim() === liquidationKey
      )
      .filter(
        (item) =>
          !search ||
          [
            item.placa,
            item.codigopaso,
            item.idtransaction,
            item.comercio,
            item.peaje?.name || item.peaje?.nombre || item.peaje,
            item.cufe,
            item.factura,
            `tipo ${item.tipo_concli}`,
          ].some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(search)
          )
      )
      .sort((a, b) => String(b.fecha_paso || "").localeCompare(String(a.fecha_paso || "")));
  }, [allRecords, searchTerm, liquidationKey, showLiquidationKeys]);

  const rows = useMemo(
    () =>
      records.map((item) => ({
        ...item,
        original: item,
      })),
    [records]
  );

  const liquidationKeys = [
    ...new Set(
      allRecords
        .map((record) => record.llave_conciliacion)
        .filter((value) => typeof value === "string" && value.trim())
        .map((value) => value.trim())
    ),
  ];
  return (
    <Box sx={{ p: 2, height: "100%" }}>
      {showLiquidationKeys && (
        <LiquidationKeyFilter
          liquidationKeys={liquidationKeys}
          liquidationKey={liquidationKey}
          onChange={setLiquidationKey}
          loading={loading}
          label={vistaConcesion ? "Llave de conciliación" : "Llave de liquidación"}
          allLabel={
            vistaConcesion ? "Todas las llaves de conciliación" : "Todas las llaves de liquidación"
          }
          emptyHelper={
            vistaConcesion
              ? "No se informaron llaves de conciliación en los pasos."
              : "No se informaron llaves de liquidación en los pasos."
          }
        />
      )}
      <Card
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "none",
          height: "100%",
        }}
      >
        <MDBox
          p={2}
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
            <Box>
              <MDTypography variant="h6" color="white" fontWeight="bold">
                Detalle de Conciliación
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                {totalRecords} registros encontrados
              </MDTypography>
            </Box>

            {concesion && (
              <Chip
                label={`Concesión: ${concesion}`}
                size="medium"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  color: "rgba(255, 255, 255, 0.95)",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  fontFamily: "monospace",
                  "& .MuiChip-label": { px: 2 },
                }}
              />
            )}

            <Chip
              label={`Total a Pagar: $${(valorTotal || 0).toLocaleString("es-CO", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}`}
              size="medium"
              sx={{
                bgcolor: "rgba(39,174,96,0.35)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.9rem",
                "& .MuiChip-label": { px: 2 },
              }}
            />

            {valorFinalFinops != null &&
              String(valorFinalFinops).trim() !== "" &&
              Number.isFinite(Number(valorFinalFinops)) && (
                <Chip
                  label={`Valor final de pago por FinOps: $${Number(
                    valorFinalFinops
                  ).toLocaleString("es-CO", { maximumFractionDigits: 2 })}`}
                  sx={{
                    bgcolor: "#fff1f2",
                    color: "#b91c1c",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    height: "auto",
                    "& .MuiChip-label": { px: 2, py: 1, whiteSpace: "normal" },
                  }}
                />
              )}
            {loadingFiles && (
              <Chip
                icon={<CircularProgress size={16} sx={{ color: "rgba(255,255,255,0.7)" }} />}
                label="Cargando documentos..."
                size="medium"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  "& .MuiChip-label": { px: 2 },
                }}
              />
            )}
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              size="small"
              placeholder={
                vistaConcesion
                  ? "Buscar por placa, peaje, código paso o fecha..."
                  : "Buscar por placa, código paso, ID tx, CUFE o factura..."
              }
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                color: "rgba(0, 0, 0, 0.7)",
                bgcolor: "rgba(255, 255, 255, 0.7)",
                borderRadius: 1,
                "& .MuiInputBase-root": {
                  height: "40px",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "transparent" },
                  "&.Mui-focused fieldset": { borderColor: "transparent" },
                },
                width: 360,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: "rgba(0, 0, 0, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </MDBox>

        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress sx={{ color: colors.secondary }} />
          </MDBox>
        ) : error ? (
          <Alert severity="error" sx={{ m: 3, borderRadius: "8px" }}>
            <MDTypography variant="body2" fontWeight="bold">
              Error al cargar los datos
            </MDTypography>
            <MDTypography variant="caption">{error}</MDTypography>
          </Alert>
        ) : records.length === 0 ? (
          <MDBox p={5} textAlign="center">
            <MDTypography variant="h6" sx={{ color: "#64748b" }}>
              No hay registros para esta conciliación
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#94a3b8", mt: 1 }}>
              {searchTerm || liquidationKey
                ? "No se encontraron resultados para los filtros seleccionados"
                : "No hay datos disponibles"}
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: "#f8fafc",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e2e8f0",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <MDTypography variant="body2" sx={{ color: "text.secondary" }}>
                Mostrando {records.length} de {totalRecords} registros
              </MDTypography>
            </Paper>

            <Box sx={{ overflowX: "auto" }}>
              <DataTable
                table={{ columns, rows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
                showTotalEntries={false}
                noEndBorder
                canSearch={false}
                paginationResetKey={JSON.stringify([searchTerm, liquidationKey])}
              />
            </Box>
          </>
        )}
      </Card>

      {/* TABLA NOVEDADES */}
      {showNovelties && (
        <NovedadesTable
          keyConcil={llaveConciliacion || records?.[0]?.llave_conciliacion || null}
          concesion={concesion}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            backgroundColor: "#f0fff4",
            color: "#276749",
            "& .MuiAlert-icon": { color: "#276749" },
          }}
        >
          <MDTypography variant="body2">{snackbar.message}</MDTypography>
        </Alert>
      </Snackbar>
    </Box>
  );
}

TableDetalleFinanciera1.propTypes = {
  valorFinalFinops: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  detailReference: PropTypes.string,
  conciliationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  concesion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refreshFlag: PropTypes.bool,
  showNovelties: PropTypes.bool,
  showLiquidationKeys: PropTypes.bool,
  vistaConcesion: PropTypes.bool,
  llaveConciliacion: PropTypes.string,
};

TableDetalleFinanciera1.defaultProps = {
  conciliationId: null,
  concesion: null,
  refreshFlag: false,
  showNovelties: true,
  vistaConcesion: false,
  llaveConciliacion: null,
};

export default TableDetalleFinanciera1;
